import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTimes, FaCheck, FaMinus, FaExclamation, FaSearch, FaPlus, FaTrash, FaDownload, FaRedo, FaHandPaper,
  FaEnvelope,
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { estadoFicha, fechaCorta, textoDias } from '../utils/fichaTi';
import { formatFecha } from './vacantesShared';
import './OnboardingWizard.css';

// Qué hace cada paso, en una línea, para quien lo está corriendo
const DESCRIPCION = {
  red: 'Cuenta de Windows en el AD, con el formato de siempre y los grupos del compañero.',
  correo: 'Cuenta nueva, o la de quien se fue renombrada a la persona nueva.',
  centro_sura: 'Usuario del portal enlazado al empleado. Con él ya puede abrir tickets en Soporte TI.',
  cima: 'Usuario de CIMA con el rol que elijas.',
  soluciones_ti: 'Usuario en Soluciones TI con los módulos del compañero.',
  profit: 'Usuario de Profit con los perfiles del compañero, creado con los procedimientos de Profit Administrador.',
  catalogo: 'Usuario de la app del catálogo digital con sus listas de precio.',
  equipos: 'Computadora, teléfono y tablet según la ficha. Se asignan desde el inventario de equipos disponibles.',
};

const CREDENCIAL = {
  red: (r) => r.username,
  correo: (r) => r.correo,
  centro_sura: (r) => r.username,
  cima: (r) => r.username,
  soluciones_ti: (r) => r.username,
  profit: (r) => r.codigo,
  catalogo: (r) => r.login,
};

const CERRADOS = ['hecho', 'manual', 'omitido'];
const TEXTO_FOTO = { true: 'Cargada', false: 'No se pudo cargar', quitada: 'Se quitó la del predecesor' };
const CELULAR = /^(0414|0424|0412|0416|0426|0422)[0-9]{7}$/;

// Datos de la imagen de bienvenida con las credenciales de los pasos hechos.
// Tambien la usa "Generar bienvenida" en Empleados, sin abrir el wizard.
export const armarBienvenida = (data, celular = '') => {
  const pasos = data.onboarding.pasos || {};
  const r = (clave) => {
    const p = pasos[clave] || {};
    return CERRADOS.includes(p.estado) && p.estado !== 'omitido' ? p.resultado || {} : null;
  };
  const seccion = (clave, usuario) => {
    const res = r(clave);
    return res && usuario(res) ? { enabled: true, username: usuario(res), password: res.clave || '' } : { enabled: false, username: '', password: '' };
  };
  const telefono = data.necesidades_equipo?.telefono && celular;
  const windows = seccion('red', (x) => x.username);
  const profit = seccion('profit', (x) => x.codigo);
  // Enlazado a su cuenta de Windows (Ad_Login), Profit entra con la clave de red
  if (profit.enabled && r('profit').ad_login) profit.password = windows.password || 'La misma de Windows';
  return {
    primer_nombre: data.empleado.primer_nombre,
    nombre_empleado: data.empleado.nombre_corto,
    windows,
    email: seccion('correo', (x) => x.correo),
    profit,
    centroSura: seccion('centro_sura', (x) => x.username),
    cima: seccion('cima', (x) => x.username),
    solucionesTi: seccion('soluciones_ti', (x) => x.username),
    catalogo: seccion('catalogo', (x) => x.login),
    fuerzaMovil: { enabled: false, id: '', username: '', password: '' },
    dispositivoMovil: { enabled: Boolean(telefono), linea: celular || '' },
    pinImpresion: { enabled: false, pin: '' },
  };
};

const mensajeError = (e, porDefecto) => e?.response?.data?.error || e?.message || porDefecto;

const descargar = (blob, nombre) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombre);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

function planInicial(clave, d) {
  switch (clave) {
    case 'red':
      return {
        username: d.username || '', cn: d.cn || '', ou: d.ou || '', grupos: d.grupos || [], atributos: { ...d.atributos },
        jefe_nombre: d.jefe?.encontrado ? d.jefe.nombre : '',
      };
    case 'correo':
      return { modo: 'nuevo', usuario: d.usuario || '', dominio: d.dominio || 'sura.com.ve', grupos: d.grupos_sugeridos || [] };
    case 'centro_sura':
      return { username: d.username || '', role: d.role || 'user' };
    case 'cima':
      return { role: d.role || '', vendedor_id: d.vendedor_id || '' };
    case 'soluciones_ti':
      return { username: d.username || '', modulos: d.seleccionados || [] };
    case 'profit':
      return {
        codigo: d.codigo || '', nombre: d.nombre || '', ad_login: d.ad_login || '',
        perfiles: (d.perfiles || []).map(({ empresa, mapa }) => ({ empresa, mapa })),
        referente_codigo: d.referente?.codigo || null,
      };
    case 'catalogo':
      return { login: d.login || '', listas: d.listas || [], rol_id: d.rol_id || 1 };
    default:
      return {};
  }
}

// ─── Piezas chicas ───────────────────────────────────────────────────────────

const Marca = ({ estado, aplica, activo }) => {
  if (!aplica) return <span className="obw-marca obw-marca--off" aria-hidden="true" />;
  if (estado === 'hecho' || estado === 'manual') {
    return <span className="obw-marca obw-marca--ok" aria-hidden="true"><FaCheck /></span>;
  }
  if (estado === 'omitido') return <span className="obw-marca obw-marca--omitido" aria-hidden="true"><FaMinus /></span>;
  if (estado === 'error') return <span className="obw-marca obw-marca--error" aria-hidden="true"><FaExclamation /></span>;
  return <span className={`obw-marca ${activo ? 'obw-marca--actual' : ''}`} aria-hidden="true" />;
};

const Campo = ({ label, children, ayuda }) => (
  <label className="obw-campo">
    <span className="obw-campo-label">{label}</span>
    {children}
    {ayuda && <span className="obw-campo-ayuda">{ayuda}</span>}
  </label>
);

const Clave = ({ valor, nota }) => (
  <div className="obw-clave">
    <span className="obw-clave-label">Clave inicial</span>
    <code className="obw-cred">{valor || '—'}</code>
    {nota && <span className="obw-clave-nota">{nota}</span>}
  </div>
);

const Persona = ({ p, seleccionado, onClick, detalle }) => (
  <button type="button" className={`obw-persona ${seleccionado ? 'is-sel' : ''}`} onClick={onClick}>
    <span className="obw-persona-nombre">{p.nombre}</span>
    <span className="obw-persona-cargo">{p.cargo || 'Sin cargo'}{p.area ? `, ${p.area}` : ''}</span>
    {detalle && <span className="obw-persona-detalle">{detalle}</span>}
  </button>
);

export function BuscadorEmpleados({ activos, onElegir, placeholder }) {
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState([]);
  useEffect(() => {
    if (texto.trim().length < 2) { setResultados([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await axiosInstance.get('/api/onboarding/buscar-empleados', { params: { q: texto, activos: activos ? 1 : 0 } });
        setResultados(r.data);
      } catch { setResultados([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [texto, activos]);
  return (
    <div className="obw-buscador">
      <div className="obw-input-icono">
        <FaSearch aria-hidden="true" />
        <input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder={placeholder} />
      </div>
      {resultados.length > 0 && (
        <div className="obw-lista">
          {resultados.map((p) => (
            <Persona key={p.id} p={p} onClick={() => { onElegir(p); setTexto(''); }}
              detalle={activos ? null : (p.fecha_egreso ? `Egresó el ${formatFecha(p.fecha_egreso)}` : null)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Pasos ───────────────────────────────────────────────────────────────────

function Jefe({ d, plan, set }) {
  const [cambiando, setCambiando] = useState(false);
  const [error, setError] = useState(null);
  const elegir = async (p) => {
    try {
      const r = await axiosInstance.get('/api/onboarding/ad/cuenta', { params: { empleado_id: p.id } });
      set({ ...plan, jefe_nombre: r.data.nombre, atributos: { ...plan.atributos, manager: r.data.dn } });
      setCambiando(false);
      setError(null);
    } catch (e) {
      setError(mensajeError(e, 'No se encontró su cuenta en el AD'));
    }
  };
  let ayuda = null;
  if (!plan.atributos.manager && d.jefe?.nombre && !d.jefe.encontrado) ayuda = `No se encontró la cuenta de ${d.jefe.nombre} en el AD.`;
  return (
    <div className="obw-campo">
      <span className="obw-campo-label">Jefe</span>
      <div className="obw-jefe">
        <span className={plan.atributos.manager ? '' : 'obw-tenue'}>{plan.atributos.manager ? plan.jefe_nombre || 'Sin nombre' : 'Sin jefe en el AD'}</span>
        <button type="button" className="obw-link" onClick={() => setCambiando(!cambiando)}>{cambiando ? 'Cancelar' : 'Cambiar'}</button>
        {plan.atributos.manager && (
          <button type="button" className="obw-link" onClick={() => set({ ...plan, jefe_nombre: '', atributos: { ...plan.atributos, manager: '' } })}>Quitar</button>
        )}
      </div>
      {ayuda && <span className="obw-campo-ayuda">{ayuda}</span>}
      {error && <span className="obw-campo-ayuda obw-error-texto">{error}</span>}
      {cambiando && <BuscadorEmpleados activos placeholder="Buscar al jefe" onElegir={elegir} />}
    </div>
  );
}

function PasoRed({ d, plan, set }) {
  const [busca, setBusca] = useState('');
  const [hallados, setHallados] = useState([]);
  useEffect(() => {
    if (busca.trim().length < 2) { setHallados([]); return undefined; }
    const t = setTimeout(async () => {
      try { setHallados((await axiosInstance.get('/api/onboarding/ad/grupos', { params: { q: busca } })).data); }
      catch { setHallados([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [busca]);
  const attr = (k, v) => set({ ...plan, atributos: { ...plan.atributos, [k]: v } });
  const tieneGrupo = (dn) => plan.grupos.some((g) => g.dn === dn);
  return (
    <>
      <div className="obw-grid2">
        <Campo label="Usuario"><input value={plan.username} onChange={(e) => set({ ...plan, username: e.target.value.toLowerCase().trim() })} /></Campo>
        <Campo label="Nombre en el AD"><input value={plan.cn} onChange={(e) => set({ ...plan, cn: e.target.value })} /></Campo>
      </div>
      <Campo label="Unidad organizativa" ayuda={d.referente_con_cuenta ? 'La misma del compañero de referencia.' : 'El compañero no tiene cuenta: se tomó la de la sede.'}>
        <select value={plan.ou} onChange={(e) => set({ ...plan, ou: e.target.value })}>
          {!d.ous.some((o) => o.dn === plan.ou) && <option value={plan.ou}>{plan.ou}</option>}
          {d.ous.map((o) => <option key={o.dn} value={o.dn}>{o.nombre}</option>)}
        </select>
      </Campo>
      <div className="obw-grid2">
        <Campo label="Cargo"><input value={plan.atributos.title || ''} onChange={(e) => set({ ...plan, atributos: { ...plan.atributos, title: e.target.value, description: e.target.value } })} /></Campo>
        <Campo label="Departamento"><input value={plan.atributos.department || ''} onChange={(e) => attr('department', e.target.value)} /></Campo>
        <Campo label="Oficina (sede)"><input value={plan.atributos.physicalDeliveryOfficeName || ''} onChange={(e) => attr('physicalDeliveryOfficeName', e.target.value)} /></Campo>
        <Campo label="Extensión" ayuda={d.heredado_de && plan.atributos.telephoneNumber ? `La de ${d.heredado_de}.` : null}><input value={plan.atributos.telephoneNumber || ''} onChange={(e) => attr('telephoneNumber', e.target.value)} /></Campo>
        <Campo label="Celular" ayuda={d.heredado_de && plan.atributos.mobile ? `La línea de ${d.heredado_de}.` : null}><input value={plan.atributos.mobile || ''} onChange={(e) => attr('mobile', e.target.value)} /></Campo>
        <Campo label="Correo" ayuda={plan.atributos.mail ? null : 'Todavía no tiene: se pone solo al crear el correo.'}><input value={plan.atributos.mail || ''} onChange={(e) => attr('mail', e.target.value)} /></Campo>
      </div>
      <Jefe d={d} plan={plan} set={set} />
      <div className="obw-bloque">
        <span className="obw-campo-label">Grupos {plan.grupos.length > 0 && `(${plan.grupos.length})`}</span>
        {plan.grupos.length === 0 && <p className="obw-vacio">El compañero no tiene grupos. Agrega los que hagan falta.</p>}
        <div className="obw-chips">
          {plan.grupos.map((g) => (
            <span key={g.dn} className="obw-chip">
              {g.nombre}
              <button type="button" aria-label={`Quitar ${g.nombre}`} onClick={() => set({ ...plan, grupos: plan.grupos.filter((x) => x.dn !== g.dn) })}><FaTimes /></button>
            </span>
          ))}
        </div>
        <div className="obw-input-icono obw-mt">
          <FaSearch aria-hidden="true" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar un grupo para agregar" />
        </div>
        {hallados.length > 0 && (
          <div className="obw-lista">
            {hallados.filter((g) => !tieneGrupo(g.dn)).map((g) => (
              <button key={g.dn} type="button" className="obw-fila-btn" onClick={() => { set({ ...plan, grupos: [...plan.grupos, g] }); setBusca(''); }}>
                <FaPlus aria-hidden="true" /> {g.nombre}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="obw-nota">{d.foto ? 'Se le pone la foto que subió GH (sale en Windows y Outlook).' : 'Sin foto: GH todavía no la ha subido.'}</p>
      <Clave valor={d.clave} nota="Puede cambiarla, pero no se le exige. Vence según la GPO." />
    </>
  );
}

function PasoCorreo({ d, plan, set, predecesor }) {
  const puedeHeredar = d.predecesor?.existe;
  return (
    <>
      <div className="obw-opciones">
        <label className={`obw-opcion ${plan.modo === 'nuevo' ? 'is-sel' : ''}`}>
          <input type="radio" checked={plan.modo === 'nuevo'} onChange={() => set({ ...plan, modo: 'nuevo' })} />
          <span><strong>Crear cuenta nueva</strong><small>Buzón vacío, usa una licencia.</small></span>
        </label>
        <label className={`obw-opcion ${plan.modo === 'predecesor' ? 'is-sel' : ''} ${!puedeHeredar ? 'is-off' : ''}`}>
          <input type="radio" disabled={!puedeHeredar} checked={plan.modo === 'predecesor'}
            onChange={() => set({ ...plan, modo: 'predecesor', dominio: 'sura.com.ve' })} />
          <span>
            <strong>Tomar la cuenta de {predecesor ? predecesor.nombre_corto : 'quien se fue'}</strong>
            <small>
              {!predecesor && 'Elige a quién reemplaza en Contexto.'}
              {predecesor && !d.predecesor && 'No tiene cuenta @sura.com.ve.'}
              {d.predecesor && !d.predecesor.existe && `${d.predecesor.correo} ya no existe en Google.`}
              {puedeHeredar && `Hereda buzón, Drive y calendario de ${d.predecesor.correo}, que queda como alias.`}
            </small>
          </span>
        </label>
      </div>
      <Campo label="Dirección" ayuda={[d.ocupado && 'nombre.apellido ya está ocupado en Google; se propone otra.', plan.modo === 'nuevo' && d.dominio_motivo].filter(Boolean).join(' ') || null}>
        <div className="obw-correo">
          <input value={plan.usuario} onChange={(e) => set({ ...plan, usuario: e.target.value.toLowerCase().trim() })} />
          <span>@</span>
          <select value={plan.dominio} disabled={plan.modo === 'predecesor'} onChange={(e) => set({ ...plan, dominio: e.target.value })}>
            {d.dominios.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      </Campo>
      {plan.dominio === 'sura.com.ve' && (d.grupos?.length > 0 || d.grupos_error) && (
        <div className="obw-bloque">
          <span className="obw-campo-label">Grupos de correo</span>
          <p className="obw-campo-ayuda">{d.grupos_error || 'Marcados todos@ y los del compañero de referencia.'}</p>
          <div className="obw-checks">
            {(d.grupos || []).map((g) => (
              <label key={g.email} className="obw-check">
                <input type="checkbox" checked={plan.grupos.includes(g.email)}
                  onChange={() => set({ ...plan, grupos: plan.grupos.includes(g.email) ? plan.grupos.filter((x) => x !== g.email) : [...plan.grupos, g.email] })} />
                <span>
                  {g.nombre}
                  {plan.modo === 'predecesor' && g.del_predecesor && <small className="obw-tenue"> (ya la tiene la cuenta heredada)</small>}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      {plan.dominio === 'sura.com.ve' && (
        <p className="obw-nota">
          {d.foto ? 'Se le pone la foto que subió GH.' : plan.modo === 'predecesor' ? 'Sin foto nueva: se quita la del predecesor.' : 'Sin foto: GH todavía no la ha subido.'}
        </p>
      )}
      <Clave valor={d.clave} nota="No se le exige cambiarla al entrar." />
    </>
  );
}

function PasoCentroSura({ d, plan, set }) {
  return (
    <>
      <div className="obw-grid2">
        <Campo label="Usuario"><input value={plan.username} onChange={(e) => set({ ...plan, username: e.target.value.toLowerCase().trim() })} /></Campo>
        <Campo label="Rol" ayuda={d.role_referente ? `El compañero tiene ${d.role_referente}.` : 'El compañero no tiene usuario: se propone user.'}>
          <select value={plan.role} onChange={(e) => set({ ...plan, role: e.target.value })}>
            {d.roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Campo>
      </div>
      <Clave valor={d.clave} nota="La cédula sin puntos." />
    </>
  );
}

function PasoCima({ d, plan, set }) {
  const etiqueta = (v) => d.roles.find((r) => r.value === v)?.label || v;
  return (
    <>
      <Campo label="Rol" ayuda={[
        d.role_referente && `El compañero es ${etiqueta(d.role_referente)}.`,
        d.role_sugerido && `Por el cargo, CIMA sugiere ${etiqueta(d.role_sugerido)}.`,
      ].filter(Boolean).join(' ') || null}>
        <select value={plan.role} onChange={(e) => set({ ...plan, role: e.target.value })}>
          <option value="">Elige un rol</option>
          {d.roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </Campo>
      <div className="obw-grid2">
        <Campo label="Código de vendedor" ayuda={d.vendedor_origen && plan.vendedor_id === d.vendedor_id ? `${d.vendedor_origen}.${d.silla_ocupada ? ' Todavía está sentado ahí: vacía su silla desde Cerrar cuentas, en su expediente.' : ''}` : 'Solo si vende: su silla en Profit.'}><input value={plan.vendedor_id} onChange={(e) => set({ ...plan, vendedor_id: e.target.value.toUpperCase().trim() })} /></Campo>
        <Campo label="Correo" ayuda={!d.correo ? 'CIMA lo exige: crea el correo primero.' : null}><input value={d.correo || 'Sin correo'} disabled /></Campo>
      </div>
      <p className="obw-nota">El usuario lo arma CIMA: inicial del nombre y tres letras del apellido, en mayúscula.</p>
      <Clave valor={d.clave} nota="Su cédula." />
    </>
  );
}

function PasoSolucionesTi({ d, plan, set }) {
  const marcar = (code) => set({ ...plan, modulos: plan.modulos.includes(code) ? plan.modulos.filter((m) => m !== code) : [...plan.modulos, code] });
  return (
    <>
      <Campo label="Usuario" ayuda="El mismo de red."><input value={plan.username} onChange={(e) => set({ ...plan, username: e.target.value.toLowerCase().trim() })} /></Campo>
      <div className="obw-bloque">
        <span className="obw-campo-label">Módulos</span>
        <p className="obw-campo-ayuda">{d.referente_usuario ? `Marcados los de ${d.referente_usuario}.` : 'El compañero no tiene usuario en Soluciones TI.'}</p>
        <div className="obw-checks">
          {d.modulos.map((m) => (
            <label key={m.code} className="obw-check">
              <input type="checkbox" checked={plan.modulos.includes(m.code)} onChange={() => marcar(m.code)} />
              <span>{m.name}</span>
            </label>
          ))}
        </div>
      </div>
      <Clave valor={d.clave} nota="Su cédula." />
    </>
  );
}

function PasoProfit({ d, plan, set }) {
  const productoDe = (empresa) => d.empresas.find((e) => e.codigo === empresa)?.producto;
  const fila = (i, cambios) => set({ ...plan, perfiles: plan.perfiles.map((p, j) => (j === i ? { ...p, ...cambios } : p)) });
  const faltan = (d.perfiles_referente || []).filter((r) => !plan.perfiles.some((p) => p.empresa === r.empresa));
  return (
    <>
      <div className="obw-grid3">
        <Campo label="Código"><input value={plan.codigo} maxLength={6} onChange={(e) => set({ ...plan, codigo: e.target.value.toUpperCase().trim() })} /></Campo>
        <Campo label="Nombre"><input value={plan.nombre} maxLength={30} onChange={(e) => set({ ...plan, nombre: e.target.value })} /></Campo>
        <Campo label="Usuario de Windows"><input value={plan.ad_login} onChange={(e) => set({ ...plan, ad_login: e.target.value.toUpperCase().trim() })} /></Campo>
      </div>
      <div className="obw-bloque">
        <span className="obw-campo-label">Perfiles</span>
        <p className="obw-campo-ayuda">
          {d.referente ? `Copiados de ${d.referente.nombre} (${d.referente.codigo}), solo de los módulos que pide la ficha.` : 'El compañero no tiene usuario de Profit: arma los perfiles a mano.'}
        </p>
        <div className="obw-tabla">
          {plan.perfiles.map((p, i) => (
            <div key={i} className="obw-tabla-fila">
              <select value={p.empresa} onChange={(e) => fila(i, { empresa: e.target.value, mapa: '' })}>
                <option value="">Empresa</option>
                {d.empresas.map((e) => <option key={e.codigo} value={e.codigo}>{e.codigo} ({e.producto === 'ADMI' ? 'Administrativo' : 'Contable'})</option>)}
              </select>
              <select value={p.mapa} onChange={(e) => fila(i, { mapa: e.target.value })}>
                <option value="">Rol</option>
                {d.mapas.filter((m) => m.producto === productoDe(p.empresa)).map((m) => <option key={m.codigo} value={m.codigo}>{m.nombre}</option>)}
              </select>
              <button type="button" className="obw-icono-btn" aria-label="Quitar perfil" onClick={() => set({ ...plan, perfiles: plan.perfiles.filter((_, j) => j !== i) })}><FaTrash /></button>
            </div>
          ))}
        </div>
        <div className="obw-chips obw-mt">
          <button type="button" className="obw-link" onClick={() => set({ ...plan, perfiles: [...plan.perfiles, { empresa: '', mapa: '' }] })}><FaPlus aria-hidden="true" /> Agregar perfil</button>
          {faltan.map((r) => (
            <button key={r.empresa} type="button" className="obw-link" onClick={() => set({ ...plan, perfiles: [...plan.perfiles, { empresa: r.empresa, mapa: r.mapa }] })}>
              <FaPlus aria-hidden="true" /> {r.empresa}: {r.mapa_nombre || r.mapa}
            </button>
          ))}
        </div>
      </div>
      {plan.ad_login
        ? <p className="obw-campo-ayuda">Entra con su cuenta de Windows: en la bienvenida sale la misma clave de red.</p>
        : <Clave valor={d.clave} nota="Su cédula. Profit le pide cambiarla al primer ingreso." />}
    </>
  );
}

function PasoCatalogo({ d, plan, set }) {
  const marcar = (c) => set({ ...plan, listas: plan.listas.includes(c) ? plan.listas.filter((x) => x !== c) : [...plan.listas, c].sort() });
  return (
    <>
      <div className="obw-grid2">
        <Campo label="Usuario" ayuda="Inicial del nombre y tres letras del apellido, como en CIMA."><input value={plan.login} onChange={(e) => set({ ...plan, login: e.target.value.toUpperCase().trim() })} /></Campo>
        <Campo label="Rol">
          <select value={plan.rol_id} onChange={(e) => set({ ...plan, rol_id: Number(e.target.value) })}>
            {d.roles.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
        </Campo>
      </div>
      <div className="obw-bloque">
        <span className="obw-campo-label">Listas de precio</span>
        <p className="obw-campo-ayuda">
          {d.listas_de === 'predecesor' && `Marcadas las de ${d.referente_login}, a quien reemplaza.`}
          {d.listas_de === 'referente' && `Marcadas las de ${d.referente_login}, el compañero de referencia.`}
          {!d.listas_de && 'Ni el compañero ni a quien reemplaza tienen usuario en el catálogo.'}
        </p>
        <div className="obw-checks">
          {d.listas_disponibles.map((l) => (
            <label key={l.codigo} className="obw-check">
              <input type="checkbox" checked={plan.listas.includes(l.codigo)} onChange={() => marcar(l.codigo)} />
              <span>{l.codigo} {l.nombre}</span>
            </label>
          ))}
        </div>
      </div>
      <Clave valor={d.clave} nota="Su cédula." />
    </>
  );
}

const FORMULARIOS = {
  red: PasoRed, correo: PasoCorreo, centro_sura: PasoCentroSura, cima: PasoCima,
  soluciones_ti: PasoSolucionesTi, profit: PasoProfit, catalogo: PasoCatalogo,
};

const TEXTO_EXISTE = {
  red: (d) => `Ya tiene cuenta de red: ${d.cuenta.username}${d.cuenta.activa ? '' : ' (deshabilitada)'}.`,
  correo: (d) => `Ya tiene correo: ${d.correo}.`,
  centro_sura: (d) => `Ya tiene usuario de Centro Sura: ${d.username} (${d.role}).`,
  cima: (d) => `Ya tiene usuario de CIMA: ${d.username} (${d.role}).`,
  soluciones_ti: (d) => `Ya tiene usuario en Soluciones TI: ${d.username}.`,
  profit: (d) => `Ya tiene usuario de Profit: ${d.codigo} (${d.nombre}).`,
  catalogo: (d) => `Ya tiene usuario en el catálogo: ${d.login}.`,
};

const RESULTADO_EXISTE = {
  red: (d) => ({ username: d.cuenta.username }),
  correo: (d) => ({ correo: d.correo }),
  centro_sura: (d) => ({ username: d.username }),
  cima: (d) => ({ username: d.username }),
  soluciones_ti: (d) => ({ username: d.username }),
  profit: (d) => ({ codigo: d.codigo }),
  catalogo: (d) => ({ login: d.login }),
};

const CAMPO_MANUAL = {
  red: ['username', 'Usuario'], correo: ['correo', 'Correo'], centro_sura: ['username', 'Usuario'],
  cima: ['username', 'Usuario'], soluciones_ti: ['username', 'Usuario'], profit: ['codigo', 'Código'],
  catalogo: ['login', 'Usuario'],
};

function DetalleResultado({ clave, r }) {
  const filas = [];
  if (clave === 'red') {
    filas.push(['Usuario', r.username], ['Clave', r.clave]);
    if (r.grupos?.length) filas.push(['Grupos', r.grupos.join(', ')]);
    if (r.grupos_fallidos?.length) filas.push(['No se pudo agregar a', r.grupos_fallidos.join(', ')]);
    if (r.activa === false) filas.push(['Atención', 'La cuenta quedó deshabilitada']);
    if (r.foto !== undefined && r.foto !== null) filas.push(['Foto', TEXTO_FOTO[r.foto]]);
    if (r.jefe) filas.push(['Jefe en el AD', r.jefe]);
    if (r.jefe_fallido) filas.push(['Atención', `No se pudo poner a ${r.jefe_fallido} como jefe en el AD`]);
  } else if (clave === 'correo') {
    filas.push(['Correo', r.correo], ['Clave', r.clave]);
    if (r.alias) filas.push(['Alias', `${r.alias} (cuenta heredada)`]);
    if (r.reenvio_apagado === false) filas.push(['Reenvío', 'Su correo podía seguir reenviándose al jefe del predecesor y no se pudo revisar: míralo en Gmail']);
    if (r.ad_actualizado === false) filas.push(['Atención', 'No se pudo poner el correo en el AD']);
    if (r.grupos?.length) filas.push(['Grupos', r.grupos.join(', ')]);
    if (r.grupos_fallidos?.length) filas.push(['Atención', `No se pudo agregar a ${r.grupos_fallidos.join(', ')}`]);
    if (r.foto !== undefined && r.foto !== null) filas.push(['Foto', TEXTO_FOTO[r.foto]]);
  } else if (clave === 'profit') {
    filas.push(['Código', r.codigo], ['Clave', r.clave], ['Perfiles', (r.perfiles || []).map((p) => `${p.empresa} rol ${p.mapa}`).join(', ')]);
  } else if (clave === 'catalogo') {
    filas.push(['Usuario', r.login], ['Clave', r.clave], ['Listas', (r.listas || []).join(', ') || 'Ninguna']);
  } else if (clave === 'soluciones_ti') {
    filas.push(['Usuario', r.username], ['Clave', r.clave], ['Módulos', (r.modulos || []).length]);
  } else {
    filas.push(['Usuario', r.username], ['Clave', r.clave]);
    if (r.role) filas.push(['Rol', r.role]);
    if (r.silla) filas.push(['Silla', `${r.silla} (ya quedó sentado)`]);
    if (r.aviso_silla) filas.push(['Atención', r.aviso_silla]);
  }
  return (
    <dl className="obw-resultado">
      {filas.filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => (
        <React.Fragment key={k}><dt>{k}</dt><dd>{typeof v === 'string' && !['Grupos', 'Perfiles', 'Atención', 'Foto', 'Jefe en el AD', 'Reenvío', 'Silla'].includes(k) ? <code className="obw-cred">{v}</code> : v}</dd></React.Fragment>
      ))}
    </dl>
  );
}

// ─── Equipos ─────────────────────────────────────────────────────────────────

const SECCIONES_EQUIPO = [
  { clave: 'pc', titulo: 'Computadora', tabla: 'asset' },
  { clave: 'telefono', titulo: 'Teléfono', tabla: 'smartphone' },
  { clave: 'tablet', titulo: 'Tablet', tabla: 'tablet' },
];

const describeEquipo = (e) => [e.categoria, e.nombre, `${e.marca || ''} ${e.modelo || ''}`.trim()].filter(Boolean).join(' ');

function SeccionEquipo({ seccion, pedido, asignados, disponibles, ocupado, onMover }) {
  const [texto, setTexto] = useState('');
  const [todos, setTodos] = useState(false);
  const tipoPedido = seccion.clave === 'pc' && typeof pedido === 'string' ? pedido : null;
  const lista = disponibles.filter((e) => {
    if (tipoPedido && !todos && (e.categoria || '').toUpperCase() !== tipoPedido) return false;
    const t = texto.trim().toLowerCase();
    return !t || [e.nombre, e.marca, e.modelo, e.serial, e.activo_fijo, e.linea].some((x) => (x || '').toLowerCase().includes(t));
  });
  return (
    <section className="obw-seccion">
      <h4>{seccion.titulo}</h4>
      <p className="obw-campo-ayuda">
        {tipoPedido ? `La ficha pide ${tipoPedido === 'LAPTOP' ? 'laptop' : 'All in One'}.` : pedido ? 'La ficha lo pide.' : 'La ficha no lo pide.'}
      </p>
      {asignados.map((e) => (
        <div key={`${e.tipo}-${e.id}`} className="obw-equipo is-asignado">
          <span><strong>{describeEquipo(e)}</strong><small>Serial {e.serial || 'N/A'}, AF {e.activo_fijo || 'N/A'}{e.linea ? `, línea ${e.linea}` : ''}</small></span>
          <button type="button" className="obw-link" disabled={ocupado === `${e.tipo}-${e.id}`} onClick={() => onMover('desasignar', e)}>Quitar</button>
        </div>
      ))}
      {asignados.length === 0 && (
        <>
          <div className="obw-filtro-equipo">
            <div className="obw-input-icono"><FaSearch aria-hidden="true" /><input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Serial, activo fijo, modelo" /></div>
            {tipoPedido && (
              <label className="obw-check"><input type="checkbox" checked={todos} onChange={() => setTodos(!todos)} /><span>Ver todas las computadoras</span></label>
            )}
          </div>
          <div className="obw-equipos">
            {lista.length === 0 && <p className="obw-vacio">No hay disponibles con ese filtro.</p>}
            {lista.map((e) => (
              <div key={`${e.tipo}-${e.id}`} className="obw-equipo">
                <span><strong>{describeEquipo(e)}</strong><small>Serial {e.serial || 'N/A'}, AF {e.activo_fijo || 'N/A'}{e.linea ? `, línea ${e.linea}` : ''}{e.sede ? `, ${e.sede}` : ''}</small></span>
                <button type="button" className="obw-btn obw-btn--sec" disabled={Boolean(ocupado)} onClick={() => onMover('asignar', e)}>Asignar</button>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function PasoEquipos({ empleadoId, necesidades, onCambio }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(null);

  const cargar = useCallback(async () => {
    try { setDatos((await axiosInstance.get(`/api/onboarding/${empleadoId}/equipos`)).data); setError(null); }
    catch (e) { setError(mensajeError(e, 'No se pudieron cargar los equipos')); }
  }, [empleadoId]);
  useEffect(() => { cargar(); }, [cargar]);

  const mover = async (accion, eq) => {
    setOcupado(`${eq.tipo}-${eq.id}`);
    try {
      setDatos((await axiosInstance.post(`/api/onboarding/${empleadoId}/equipos/${accion}`, { tipo: eq.tipo, id: eq.id })).data);
      setError(null);
      onCambio();
    } catch (e) { setError(mensajeError(e, 'No se pudo mover el equipo')); }
    finally { setOcupado(null); }
  };

  const pedidas = SECCIONES_EQUIPO.filter((x) => necesidades[x.clave]);
  // Si se activó a mano sin que la ficha pida nada, se ofrecen las tres
  const secciones = pedidas.length ? pedidas : SECCIONES_EQUIPO;
  return (
    <>
      {error && <div className="obw-alerta obw-alerta--error">{error}</div>}
      {!datos && !error && <div className="obw-cargando">Consultando inventario…</div>}
      {datos && secciones.map((sec) => (
        <SeccionEquipo key={sec.clave} seccion={sec} pedido={necesidades[sec.clave]} ocupado={ocupado} onMover={mover}
          asignados={datos.asignados.filter((e) => e.tipo === sec.tabla)}
          disponibles={datos.disponibles.filter((e) => e.tipo === sec.tabla)} />
      ))}
    </>
  );
}

// ─── Wizard ──────────────────────────────────────────────────────────────────

export default function OnboardingWizard({ employee, onClose, onFichaCerrada }) {
  const [data, setData] = useState(null);
  const [sugerencias, setSugerencias] = useState({ referentes: [], predecesores: [] });
  const [activo, setActivo] = useState('contexto');
  const [previews, setPreviews] = useState({});
  const [planes, setPlanes] = useState({});
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState(null);
  const [manual, setManual] = useState(null); // {valor, clave}
  const [firma, setFirma] = useState({ cargo: '', extension: '', celular: '' });

  const empleadoId = employee.id;

  const aplicarRespuesta = useCallback((d) => {
    setData(d);
    if (d.sugerencias) setSugerencias(d.sugerencias);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get(`/api/onboarding/${empleadoId}`);
        aplicarRespuesta(r.data);
        setFirma({ cargo: r.data.empleado.cargo || '', extension: r.data.empleado.extension_sugerida || '', celular: (r.data.empleado.linea_sugerida || '').replace(/\D/g, '') });
      } catch (e) {
        setError(mensajeError(e, 'No se pudo abrir el onboarding'));
      }
    })();
  }, [empleadoId, aplicarRespuesta]);

  useEffect(() => {
    const alEscape = (e) => { if (e.key === 'Escape' && !trabajando) onClose(); };
    document.addEventListener('keydown', alEscape);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', alEscape); document.body.style.overflow = ''; };
  }, [onClose, trabajando]);

  const pasos = data?.onboarding.pasos || {};
  const paso = (clave) => pasos[clave] || {};

  const cargarPreview = useCallback(async (clave) => {
    if (!FORMULARIOS[clave]) return;
    setPreviews((p) => ({ ...p, [clave]: { cargando: true } }));
    try {
      const r = await axiosInstance.get(`/api/onboarding/${empleadoId}/pasos/${clave}/preview`);
      setPreviews((p) => ({ ...p, [clave]: { datos: r.data } }));
      if (!r.data.existe) setPlanes((p) => ({ ...p, [clave]: planInicial(clave, r.data) }));
    } catch (e) {
      setPreviews((p) => ({ ...p, [clave]: { error: mensajeError(e, 'No se pudo preparar este paso') } }));
    }
  }, [empleadoId]);

  useEffect(() => {
    if (!data || !FORMULARIOS[activo]) return;
    const p = paso(activo);
    if (p.aplica && !CERRADOS.includes(p.estado) && !previews[activo]) cargarPreview(activo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, data, previews, cargarPreview]);

  const irA = (clave) => { setActivo(clave); setError(null); setManual(null); };

  const patch = async (cuerpo, invalidar = true) => {
    setTrabajando(true);
    try {
      const r = await axiosInstance.patch(`/api/onboarding/${empleadoId}`, cuerpo);
      aplicarRespuesta(r.data);
      if (invalidar) { setPreviews({}); setPlanes({}); }
      setError(null);
    } catch (e) {
      setError(mensajeError(e, 'No se pudo guardar'));
    } finally {
      setTrabajando(false);
    }
  };

  const ejecutar = async (clave) => {
    setTrabajando(true);
    setError(null);
    try {
      const r = await axiosInstance.post(`/api/onboarding/${empleadoId}/pasos/${clave}/ejecutar`, { plan: planes[clave] });
      aplicarRespuesta(r.data);
      // Lo que se creó cambia lo que proponen los otros pasos (correo en el AD, usuario de red...)
      setPreviews((p) => Object.fromEntries(Object.entries(p).filter(([k]) => k === clave)));
    } catch (e) {
      let enPanel = false;
      try {
        const r = await axiosInstance.get(`/api/onboarding/${empleadoId}`);
        aplicarRespuesta(r.data);
        enPanel = r.data.onboarding.pasos[clave]?.estado === 'error';
      } catch { /* sin cambios */ }
      if (!enPanel) setError(mensajeError(e, 'No se pudo crear'));
    } finally {
      setTrabajando(false);
    }
  };

  const marcar = async (clave, estado, resultado) => {
    setTrabajando(true);
    try {
      const r = await axiosInstance.post(`/api/onboarding/${empleadoId}/pasos/${clave}/marcar`, { estado, resultado });
      aplicarRespuesta(r.data);
      setManual(null);
      setError(null);
      if (estado === 'pendiente') setPreviews((p) => { const n = { ...p }; delete n[clave]; return n; });
    } catch (e) {
      setError(mensajeError(e, 'No se pudo marcar'));
    } finally {
      setTrabajando(false);
    }
  };

  const pendientes = (data?.pasos || []).filter((p) => paso(p.clave).aplica && !CERRADOS.includes(paso(p.clave).estado));
  const cerrada = Boolean(data?.onboarding.cerrado_en) || Number(data?.empleado.estado) === 0;

  const cerrarFicha = async () => {
    setTrabajando(true);
    try {
      const r = await axiosInstance.post(`/api/onboarding/${empleadoId}/cerrar`);
      aplicarRespuesta(r.data);
      onFichaCerrada?.(empleadoId);
      setError(null);
    } catch (e) {
      setError(mensajeError(e, 'No se pudo cerrar la ficha'));
    } finally {
      setTrabajando(false);
    }
  };

  const credencial = (clave) => {
    const p = paso(clave);
    if (!CERRADOS.includes(p.estado) || !p.resultado) return null;
    return CREDENCIAL[clave]?.(p.resultado) || null;
  };

  const cuerpoBienvenida = () => armarBienvenida(data, firma.celular);

  const bienvenida = async () => {
    const cuerpo = cuerpoBienvenida();
    try {
      const resp = await axiosInstance.post('/api/generar-bienvenida', cuerpo, { responseType: 'blob' });
      descargar(resp.data, `bienvenida_${data.empleado.nombre_corto.replace(/\s+/g, '_').toLowerCase()}.png`);
    } catch (e) {
      setError('No se pudo generar la bienvenida');
    }
  };

  const enviarCorreo = async () => {
    if (firma.celular && !CELULAR.test(firma.celular)) {
      setError('El celular debe empezar por 0414, 0424, 0412, 0416, 0426 o 0422 y tener 11 dígitos');
      return;
    }
    setTrabajando(true);
    try {
      const r = await axiosInstance.post(`/api/onboarding/${empleadoId}/enviar`, { bienvenida: cuerpoBienvenida(), firma });
      aplicarRespuesta(r.data);
      setError(null);
    } catch (e) {
      setError(mensajeError(e, 'No se pudieron enviar los correos'));
    } finally {
      setTrabajando(false);
    }
  };

  const descargarFirma = async () => {
    if (firma.celular && !CELULAR.test(firma.celular)) {
      setError('El celular debe empezar por 0414, 0424, 0412, 0416, 0426 o 0422 y tener 11 dígitos');
      return;
    }
    try {
      const resp = await axiosInstance.post('/api/generar-firma', {
        nombre: data.empleado.nombre_corto, cargo: firma.cargo, extension: firma.extension, numero_celular: firma.celular,
      }, { responseType: 'blob' });
      descargar(resp.data, `firma_${data.empleado.nombre_corto.replace(/\s+/g, '_').toLowerCase()}.png`);
      setError(null);
    } catch (e) {
      setError('No se pudo generar la firma');
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  const emp = data?.empleado;
  const ob = data?.onboarding;
  const titulos = Object.fromEntries((data?.pasos || []).map((p) => [p.clave, p.titulo]));

  const panelContexto = () => (
    <div className="obw-panel">
      <h3>Antes de empezar</h3>
      <p className="obw-desc">De quién se copian los permisos y a quién reemplaza. Lo que elijas aquí rellena los demás pasos.</p>

      <section className="obw-seccion">
        <h4>Compañero de referencia</h4>
        <p className="obw-campo-ayuda">Alguien con el mismo puesto o uno parecido. De él salen la OU, los grupos, los perfiles de Profit y los módulos.</p>
        {ob.referente && <div className="obw-elegido"><Persona p={ob.referente} seleccionado onClick={() => {}} /></div>}
        <div className="obw-lista">
          {sugerencias.referentes.filter((p) => p.id !== ob.referente?.id).map((p) => (
            <Persona key={p.id} p={p} detalle={p.motivo} onClick={() => patch({ referente_id: p.id })} />
          ))}
        </div>
        <BuscadorEmpleados activos placeholder="Buscar a otra persona" onElegir={(p) => patch({ referente_id: p.id })} />
      </section>

      <section className="obw-seccion">
        <h4>Supervisor</h4>
        <p className="obw-campo-ayuda">Su jefe directo: aprueba sus solicitudes, sale en el organigrama y queda como jefe en el AD.</p>
        {ob.supervisor ? (
          <div className="obw-elegido">
            <Persona p={ob.supervisor} seleccionado onClick={() => {}} />
          </div>
        ) : <div className="obw-alerta obw-alerta--error obw-mt">Sin supervisor. GH no lo cargó: elígelo aquí.</div>}
        <div className="obw-lista">
          {(sugerencias.supervisores || []).filter((p) => p.id !== ob.supervisor?.id).map((p) => (
            <Persona key={p.id} p={p} detalle={p.motivo} onClick={() => patch({ supervisor_id: p.id })} />
          ))}
        </div>
        <BuscadorEmpleados activos placeholder="Buscar a otro supervisor" onElegir={(p) => patch({ supervisor_id: p.id })} />
      </section>

      <section className="obw-seccion">
        <h4>Reemplaza a</h4>
        <p className="obw-campo-ayuda">Opcional. Sirve para tomar su cuenta de correo en vez de crear una nueva.</p>
        {ob.predecesor ? (
          <div className="obw-elegido">
            <Persona p={ob.predecesor} seleccionado onClick={() => {}} detalle={ob.predecesor.correo} />
            <button type="button" className="obw-link" onClick={() => patch({ predecesor_id: null })}>No reemplaza a nadie</button>
          </div>
        ) : <p className="obw-vacio">No reemplaza a nadie.</p>}
        <div className="obw-lista">
          {sugerencias.predecesores.filter((p) => p.id !== ob.predecesor?.id).map((p) => (
            <Persona key={p.id} p={p} detalle={`${p.correo}${p.fecha_egreso ? `, egresó ${p.fecha_egreso}` : ''}`} onClick={() => patch({ predecesor_id: p.id })} />
          ))}
        </div>
        <BuscadorEmpleados activos={false} placeholder="Buscar entre los egresados" onElegir={(p) => patch({ predecesor_id: p.id })} />
      </section>

      <section className="obw-seccion">
        <h4>Qué se va a crear</h4>
        <p className="obw-campo-ayuda">Viene marcado según la Ficha TI.</p>
        <div className="obw-checks">
          {data.pasos.map((p) => (
            <label key={p.clave} className="obw-check">
              <input type="checkbox" checked={Boolean(paso(p.clave).aplica)} disabled={trabajando || paso(p.clave).estado === 'hecho'}
                onChange={() => patch({ aplica: { [p.clave]: !paso(p.clave).aplica } }, false)} />
              <span>{p.titulo}</span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );

  const accionesManual = (clave) => {
    const [campo, etiqueta] = CAMPO_MANUAL[clave] || [];
    if (manual) {
      return (
        <div className="obw-manual">
          {campo && (
            <div className="obw-grid2">
              <Campo label={etiqueta}><input value={manual.valor} onChange={(e) => setManual({ ...manual, valor: e.target.value })} /></Campo>
              <Campo label="Clave"><input value={manual.clave} onChange={(e) => setManual({ ...manual, clave: e.target.value })} /></Campo>
            </div>
          )}
          <div className="obw-acciones">
            <button type="button" className="obw-btn" disabled={trabajando}
              onClick={() => marcar(clave, 'manual', campo ? { [campo]: manual.valor, clave: manual.clave } : {})}>Guardar como hecho a mano</button>
            <button type="button" className="obw-btn obw-btn--ghost" onClick={() => setManual(null)}>Cancelar</button>
          </div>
        </div>
      );
    }
    return null;
  };

  const panelPaso = (clave) => {
    const p = paso(clave);
    const titulo = titulos[clave];
    if (!p.aplica) {
      return (
        <div className="obw-panel">
          <h3>{titulo}</h3>
          <p className="obw-desc">{DESCRIPCION[clave]}</p>
          <div className="obw-alerta">No aplica para esta persona.</div>
          <div className="obw-acciones">
            <button type="button" className="obw-btn" disabled={trabajando} onClick={() => patch({ aplica: { [clave]: true } }, false)}>Sí aplica</button>
          </div>
        </div>
      );
    }

    if (clave === 'equipos') {
      return (
        <div className="obw-panel">
          <h3>{titulo}</h3>
          <p className="obw-desc">{DESCRIPCION[clave]}</p>
          {p.estado === 'hecho' && <div className="obw-alerta obw-alerta--ok">Tiene asignado todo lo que pide la ficha.</div>}
          {p.estado === 'manual' && <div className="obw-alerta">Marcado como listo a mano{p.por ? ` por ${p.por}` : ''}.</div>}
          <PasoEquipos empleadoId={empleadoId} necesidades={data.necesidades_equipo || {}}
            onCambio={async () => { try { aplicarRespuesta((await axiosInstance.get(`/api/onboarding/${empleadoId}`)).data); } catch { /* sin cambios */ } }} />
          <div className="obw-acciones">
            {p.estado === 'pendiente' && (
              <button type="button" className="obw-btn obw-btn--ghost" disabled={trabajando} onClick={() => marcar(clave, 'manual')}><FaHandPaper aria-hidden="true" /> Los entrego fuera del sistema</button>
            )}
            {p.estado === 'manual' && <button type="button" className="obw-btn obw-btn--ghost" onClick={() => marcar(clave, 'pendiente')}><FaRedo aria-hidden="true" /> Volver a pendiente</button>}
          </div>
        </div>
      );
    }

    const Formulario = FORMULARIOS[clave];
    const prev = previews[clave] || {};
    const plan = planes[clave];

    let cuerpo;
    if (p.estado === 'hecho') {
      cuerpo = (
        <>
          <div className="obw-alerta obw-alerta--ok">Creado {p.por ? `por ${p.por}` : ''}.</div>
          <DetalleResultado clave={clave} r={p.resultado || {}} />
        </>
      );
    } else if (p.estado === 'manual' || p.estado === 'omitido') {
      cuerpo = (
        <>
          <div className="obw-alerta">{p.estado === 'manual' ? 'Marcado como hecho a mano' : 'Omitido'}{p.por ? ` por ${p.por}` : ''}.</div>
          {p.estado === 'manual' && p.resultado && Object.keys(p.resultado).length > 0 && <DetalleResultado clave={clave} r={p.resultado} />}
          <div className="obw-acciones">
            <button type="button" className="obw-btn obw-btn--ghost" disabled={trabajando} onClick={() => marcar(clave, 'pendiente')}><FaRedo aria-hidden="true" /> Volver a pendiente</button>
          </div>
        </>
      );
    } else if (prev.cargando || (!prev.datos && !prev.error)) {
      cuerpo = <div className="obw-cargando">Consultando…</div>;
    } else if (prev.error) {
      cuerpo = (
        <>
          <div className="obw-alerta obw-alerta--error">{prev.error}</div>
          <div className="obw-acciones">
            <button type="button" className="obw-btn obw-btn--sec" onClick={() => cargarPreview(clave)}><FaRedo aria-hidden="true" /> Reintentar</button>
            <button type="button" className="obw-btn obw-btn--ghost" onClick={() => setManual({ valor: '', clave: '' })}><FaHandPaper aria-hidden="true" /> Lo hice a mano</button>
            <button type="button" className="obw-btn obw-btn--ghost" disabled={trabajando} onClick={() => marcar(clave, 'omitido')}>Omitir</button>
          </div>
          {accionesManual(clave)}
        </>
      );
    } else if (prev.datos.existe) {
      cuerpo = (
        <>
          <div className="obw-alerta obw-alerta--ok">{TEXTO_EXISTE[clave](prev.datos)}</div>
          <div className="obw-acciones">
            {!manual && (
              <button type="button" className="obw-btn" disabled={trabajando} onClick={() => {
                const r = RESULTADO_EXISTE[clave](prev.datos);
                setManual({ valor: r[CAMPO_MANUAL[clave][0]] || '', clave: '' });
              }}>Usar esta cuenta</button>
            )}
          </div>
          {manual && <p className="obw-campo-ayuda">Anota la clave si la sabes: sale en la bienvenida.</p>}
          {accionesManual(clave)}
        </>
      );
    } else if (plan) {
      cuerpo = (
        <>
          {p.estado === 'error' && p.error && <div className="obw-alerta obw-alerta--error">El intento anterior falló: {p.error}</div>}
          <Formulario d={prev.datos} plan={plan} set={(nuevo) => setPlanes((x) => ({ ...x, [clave]: nuevo }))} predecesor={ob.predecesor} />
          {!manual && (
            <div className="obw-acciones">
              <button type="button" className="obw-btn" disabled={trabajando} onClick={() => ejecutar(clave)}>
                {trabajando ? 'Creando…' : `Crear ${clave === 'correo' && plan.modo === 'predecesor' ? 'correo heredado' : 'en ' + titulo}`}
              </button>
              <button type="button" className="obw-btn obw-btn--ghost" disabled={trabajando} onClick={() => setManual({ valor: '', clave: prev.datos.clave || '' })}><FaHandPaper aria-hidden="true" /> Lo hice a mano</button>
              <button type="button" className="obw-btn obw-btn--ghost" disabled={trabajando} onClick={() => marcar(clave, 'omitido')}>Omitir</button>
            </div>
          )}
          {accionesManual(clave)}
        </>
      );
    }

    return (
      <div className="obw-panel">
        <h3>{titulo}</h3>
        <p className="obw-desc">{DESCRIPCION[clave]}</p>
        {cuerpo}
      </div>
    );
  };

  const panelEntrega = () => {
    const filas = data.pasos.filter((p) => p.clave !== 'equipos' && paso(p.clave).aplica);
    return (
      <div className="obw-panel">
        <h3>Entrega</h3>
        <p className="obw-desc">La bienvenida sale con las credenciales de los pasos hechos. Al cerrar, la Ficha TI desaparece de la tabla.</p>
        <table className="obw-resumen">
          <tbody>
            {filas.map((p) => {
              const st = paso(p.clave);
              const res = st.resultado || {};
              return (
                <tr key={p.clave}>
                  <th scope="row">{p.titulo}</th>
                  <td>{credencial(p.clave) ? <code className="obw-cred">{credencial(p.clave)}</code> : <span className="obw-tenue">{st.estado === 'omitido' ? 'Omitido' : 'Pendiente'}</span>}</td>
                  <td>{res.clave ? <code className="obw-cred">{res.clave}</code> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <section className="obw-seccion">
          <h4>Firma de correo</h4>
          <div className="obw-grid-firma">
            <Campo label="Cargo"><input value={firma.cargo} onChange={(e) => setFirma({ ...firma, cargo: e.target.value })} /></Campo>
            <Campo label="Extensión"><input value={firma.extension} onChange={(e) => setFirma({ ...firma, extension: e.target.value })} /></Campo>
            <Campo label="Celular (opcional)"><input value={firma.celular} onChange={(e) => setFirma({ ...firma, celular: e.target.value.replace(/\D/g, '') })} /></Campo>
          </div>
        </section>

        <section className="obw-seccion">
          <h4>Enviar</h4>
          <p className="obw-campo-ayuda">
            {data.empleado.correo_corporativo
              ? `Llegan dos correos a ${data.empleado.correo_corporativo}: uno con la bienvenida y otro con la firma.`
              : 'Todavía no tiene correo corporativo: crea o anota el correo para poder enviarlas.'}
          </p>
          {data.enviado && <div className="obw-alerta obw-alerta--ok obw-mt">Enviados a {data.enviado.a}{data.enviado.por ? ` por ${data.enviado.por}` : ''}.{data.enviado.firma_gmail === true && ' La firma quedó puesta en Gmail.'}</div>}
          {typeof data.enviado?.firma_gmail === 'string' && (
            <div className="obw-alerta obw-alerta--error obw-mt">No se pudo poner la firma en Gmail: {data.enviado.firma_gmail}. Le llegó por correo para ponerla a mano.</div>
          )}
          <div className="obw-acciones">
            <button type="button" className="obw-btn" disabled={trabajando || !data.empleado.correo_corporativo || !firma.cargo} onClick={enviarCorreo}>
              <FaEnvelope aria-hidden="true" /> {data.enviado ? 'Enviar otra vez' : 'Enviar por correo'}
            </button>
            <button type="button" className="obw-btn obw-btn--sec" onClick={bienvenida}><FaDownload aria-hidden="true" /> Descargar bienvenida</button>
            <button type="button" className="obw-btn obw-btn--sec" disabled={!firma.cargo} onClick={descargarFirma}><FaDownload aria-hidden="true" /> Descargar firma</button>
          </div>
        </section>

        {(data.pendientes_manuales || []).length > 0 && (
          <section className="obw-seccion">
            <h4>Pendientes fuera del wizard</h4>
            <p className="obw-campo-ayuda">Los pide la ficha; no impiden cerrarla.</p>
            <div className="obw-checks">
              {data.pendientes_manuales.map((x) => (
                <label key={x.clave} className="obw-check">
                  <input type="checkbox" checked={x.hecho} disabled={trabajando} onChange={() => patch({ pendientes: { [x.clave]: !x.hecho } }, false)} />
                  <span>{x.titulo}</span>
                </label>
              ))}
            </div>
          </section>
        )}

        <section className="obw-seccion">
          <h4>Cerrar la Ficha TI</h4>
          {cerrada ? (
            <div className="obw-alerta obw-alerta--ok">Ficha TI cerrada.</div>
          ) : (
            <>
              {pendientes.length > 0 && <p className="obw-campo-ayuda">Falta: {pendientes.map((p) => p.titulo).join(', ')}.</p>}
              <div className="obw-acciones">
                <button type="button" className="obw-btn" disabled={trabajando || pendientes.length > 0} onClick={cerrarFicha}>Cerrar Ficha TI</button>
              </div>
            </>
          )}
        </section>
      </div>
    );
  };

  return createPortal(
    <motion.div className="obw-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="obw" role="dialog" aria-modal="true" aria-label="Onboarding TI"
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', stiffness: 320, damping: 28 }}>
        <header className="obw-cabecera">
          <div>
            <h2>{emp ? emp.nombre : employee.nombre}</h2>
            {emp && (
              <p>
                <span>{emp.cargo || 'Sin cargo'}</span>
                <span>{emp.departamento}</span>
                <span>{emp.sede}</span>
                <span>Ficha {emp.ficha}</span>
                {(() => {
                  if (cerrada) return null;
                  const f = estadoFicha(emp);
                  return (
                    <>
                      {f.yaIngreso && <span className="obw-vencida">Ingresó {f.diasDesdeIngreso ? `hace ${textoDias(f.diasDesdeIngreso)}` : 'hoy'} y la ficha sigue abierta</span>}
                      {f.diasParaIngreso !== null && <span>Ingresa el {fechaCorta(f.ingreso)}</span>}
                      {f.dias !== null && <span>Ficha abierta {f.dias ? `hace ${textoDias(f.dias)}` : 'hoy'}</span>}
                    </>
                  );
                })()}
              </p>
            )}
          </div>
          <button type="button" className="obw-cerrar" onClick={onClose} disabled={trabajando} aria-label="Cerrar"><FaTimes /></button>
        </header>

        {!data && !error && <div className="obw-cargando obw-cargando--pleno">Abriendo la ficha…</div>}
        {!data && error && <div className="obw-alerta obw-alerta--error obw-m">{error}</div>}

        {data && (
          <div className="obw-cuerpo">
            <nav className="obw-riel" aria-label="Pasos">
              <button type="button" className={`obw-riel-item ${activo === 'contexto' ? 'is-activo' : ''}`} onClick={() => irA('contexto')}>
                <span className="obw-marca obw-marca--contexto" aria-hidden="true" />
                <span className="obw-riel-texto">
                  <span className="obw-riel-titulo">Contexto</span>
                  <span className="obw-riel-sub">{ob.referente ? `Permisos de ${ob.referente.nombre_corto}` : "Sin compañero de referencia"}</span>
                </span>
              </button>
              {data.pasos.map((p) => {
                const st = paso(p.clave);
                const cred = credencial(p.clave);
                let sub = null;
                if (!st.aplica) sub = 'No aplica';
                else if (cred) sub = <code className="obw-cred obw-cred--riel">{cred}</code>;
                else if (st.estado === 'omitido') sub = 'Omitido';
                else if (st.estado === 'manual') sub = 'Hecho a mano';
                else if (st.estado === 'error') sub = 'Falló';
                else if (p.clave === 'equipos' && st.estado === 'hecho') sub = 'Asignados';
                return (
                  <button key={p.clave} type="button" onClick={() => irA(p.clave)}
                    className={`obw-riel-item ${activo === p.clave ? 'is-activo' : ''} ${!st.aplica ? 'is-off' : ''}`}>
                    <Marca estado={st.estado} aplica={st.aplica} activo={activo === p.clave} />
                    <span className="obw-riel-texto">
                      <span className="obw-riel-titulo">{p.titulo}</span>
                      {sub && <span className="obw-riel-sub">{sub}</span>}
                    </span>
                  </button>
                );
              })}
              <button type="button" className={`obw-riel-item obw-riel-entrega ${activo === 'entrega' ? 'is-activo' : ''}`} onClick={() => irA('entrega')}>
                <span className={`obw-marca ${cerrada ? 'obw-marca--ok' : ''}`} aria-hidden="true">{cerrada && <FaCheck />}</span>
                <span className="obw-riel-texto">
                  <span className="obw-riel-titulo">Entrega</span>
                  <span className="obw-riel-sub">{cerrada ? 'Ficha cerrada' : pendientes.length ? `Faltan ${pendientes.length}` : 'Lista para cerrar'}</span>
                </span>
              </button>
            </nav>

            <main className="obw-contenido">
              {error && <div className="obw-alerta obw-alerta--error obw-flotante" role="alert">{error}<button type="button" aria-label="Cerrar aviso" onClick={() => setError(null)}><FaTimes /></button></div>}
              <AnimatePresence mode="wait">
                <motion.div key={activo} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.12 }}>
                  {activo === 'contexto' && panelContexto()}
                  {activo === 'entrega' && panelEntrega()}
                  {activo !== 'contexto' && activo !== 'entrega' && panelPaso(activo)}
                </motion.div>
              </AnimatePresence>
            </main>
          </div>
        )}
      </motion.div>
    </motion.div>,
    document.body,
  );
}
